import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  Theme,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  cdn_url,
  checkPermissions,
  checkRoleAndPermission,
  FetchFileService,
  fileConverter,
  formatDate,
  GetInitials,
} from '@/utils';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { authSelector, storySelector } from '@/store/selectors';
import {
  deleteStory,
  getUploadSignedUrl,
  openModal,
  openPublishModal,
  openSubscriptionPopup,
  refreshUserData,
  setPublication,
  updateStory,
} from '@/store/actions';
import { MuiIconButton } from './IconButton';
import { storyTemplates } from '@/screens/CreateStory/formsTemplates';
import AutoPopup from './AutoPopup';
import { UseFirstRender } from '@/hooks';
import { verifyFile } from '@/utils/FetchFileService';
import { DeleteStoryModal } from '@/screens/Memories/components';

interface StoryHeaderProps {
  extendedPalette?: any;
  profileSrc: string;
  coverSrc: string;
  title: string;
  description: string;
  createdDate?: string;
  collaborators?: any;
  story?: any;
  CloseManageCollaborators: () => void;
}

interface DropdownItem {
  key: any;
  alttitle: string;
  title: string;
  icon: string;
  alticon1: string;
  hoverColor: string;
  onClick: () => void;
}
const StoryHeader: React.FC<StoryHeaderProps> = ({
  extendedPalette,
  CloseManageCollaborators,
  profileSrc,
  coverSrc,
  title,
  description,
  createdDate,
  collaborators,
}) => {
  const [coverLoading, setCoverLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState<any>({});
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { user } = useSelector(authSelector);
  const { story } = useSelector(storySelector);
  const router = useRouter();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openDeleteModal, SetOpenDeleteModal] = useState(false);
  const open = Boolean(anchorEl);

  const EditMemvy = () => {
    dispatch(refreshUserData());
    if (checkRoleAndPermission(story?.user?.roles, 'Subscriber_Individual', 'CLIENT_STORY_UPDATE', story?.user_id)) {
      router.push(`/app/story/${story?.url}/update`);
    } else {
      if (user?.id == story?.user_id) {
        dispatch(
          openModal({
            content: 'Your subscription is expired. Please subscribe to continue editing your story.',
          }),
        );
        localStorage.setItem('router_history', `/app/story/${story?.url}`);
        setTimeout(() => {
          dispatch(openSubscriptionPopup());
        }, 2000);
      } else {
        dispatch(openModal({ content: 'The owner of the story is not a subscriber.' }));
      }
    }
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // const handleNavigate = () => {
  //   router.push('/app/home');
  //   handleClose();
  // };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const images =
    collaborators &&
    collaborators.map((collaborator: any) => ({
      src: collaborator.picture ? `${cdn_url}${collaborator.picture}` : '/default-profile.png',
      alt: GetInitials(collaborator.name, collaborator?.lastname),
      title: `${collaborator.name} ${collaborator.lastname}`,
      id: collaborator?.id,
    }));
  // const navigateToHome = () => {
  //   router.push('/');
  //   handleClose();
  // };

  const saveDrafts = () => {
    if (story?.status === 'drafts') {
      handleClose();
    }
    dispatch(
      setPublication({ id: story?.id, newCode: false, isLocked: story?.isLocked, status: 'draft', isPrivate: false }),
    );
  };

  const publishPrivate = () => {
    dispatch(
      setPublication({
        id: story?.id,
        newCode: false,
        isLocked: story?.isLocked,
        status: 'published',
        isPrivate: true,
      }),
    );
    handleClose();
  };

  const publishPublic = () => {
    dispatch(
      setPublication({
        id: story?.id,
        newCode: false,
        isLocked: story?.isLocked,
        status: 'published',
        isPrivate: false,
      }),
    );
    handleClose();
  };

  const HandleDeleteMemvy = () => [SetOpenDeleteModal(true)];
  const deleteMemvy = async () => {
    dispatch(deleteStory(story?.id));
    router.push('/app/home');
  };

  useEffect(() => {
    if (story)
      setSelectedStory(
        storyTemplates.find((item) => item.key === story?.story_details?.type_of_story) || storyTemplates[0],
      );
  }, [story]);

  const menuItems = [
    {
      key: 'draft',
      alttitle: 'Draft',
      title: 'Save Drafts',
      icon: '/icons/drafts.svg',
      alticon1: '/icons/drafts-white',
      hoverColor: '#0072CE',
      onClick: saveDrafts,
    },
    {
      key: 'true',
      alttitle: 'Private',
      title: 'Publish Private',
      icon: '/icons/simple-lock.svg',
      alticon1: '/icons/simple-lock-white',
      secondaryText: 'Invite only',
      hoverColor: '#0072CE',
      onClick: publishPrivate,
    },
    {
      key: 'false',
      alttitle: 'Public',
      title: 'Publish Public',
      icon: '/icons/globe.svg',
      alticon1: '/icons/globe-white',
      secondaryText: 'Anyone Can View',
      hoverColor: '#0072CE',
      onClick: publishPublic,
    },
    user?.id == story?.user_id && {
      title: 'Delete Memvy',
      icon: '/icons/delete.svg',
      alticon1: '/icons/delete',
      hoverColor: '#F4C0C0',
      textColor: '#E77476',
      onClick: HandleDeleteMemvy,
    },
  ].filter(Boolean);

  const [selectedItem, setSelectedItem] = useState<DropdownItem>();

  useEffect(() => {
    if (!story) return;

    let selected: any = null;

    if (story.status === 'published') {
      if (story.private) {
        selected = menuItems.find((item: any) => item.key === 'true');
      } else if (story.isLocked) {
        selected = menuItems.find((item: any) => item.key === 'public-locked');
      } else {
        selected = menuItems.find((item: any) => item.key === 'false');
      }
    } else {
      selected = menuItems.find((item: any) => item.key === 'draft');
    }
    setSelectedItem(selected ? selected : []);
  }, [story]);

  const handleSelect = (title: any, onClick: any) => {
    const newSelection: any = menuItems.find((item: any) => item.title === title);
    if (newSelection) {
      setSelectedItem(newSelection);
    }
    if (onClick) {
      onClick();
    }
    handleClose();
  };

  const [openPop, setOpenPop] = useState(false);

  UseFirstRender(() => {
    if (story?.status === 'draft') {
      setOpenPop(true);
    } else {
      setOpenPop(false);
    }
  }, [story?.status]);

  const validateMediaFile = (file: File, type: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (type === 'image') {
        const img = document.createElement('img') as HTMLImageElement;
        img.src = URL.createObjectURL(file);
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
      } else if (type === 'video' || type === 'audio') {
        const media = document.createElement(type) as HTMLMediaElement;
        media.src = URL.createObjectURL(file);
        media.onloadeddata = () => resolve(true);
        media.onerror = () => resolve(false);
      } else {
        resolve(false);
      }
    });
  };
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const generateFilePath = (fileName: string, title: string, type: string, timestamp: number) => {
    const sanitizedFileName = fileName?.split('/')?.pop();

    if (type === 'video') return `videos/${timestamp}_${sanitizedFileName}`;

    return `stories/${title}/memory/${timestamp}_${sanitizedFileName?.replace(/\.[^.]+$/, '.webp')}`;
  };
  const getFileType = (type: string, mediaType: string) => (type === 'image' ? 'image/webp' : mediaType);
  // const uploadFile = async (uploadUrl: string, media: any, type: string) => {
  //   const fileToUpload = type === 'image' ? await fileConverter(media) : media;
  //   const response = await FetchFileService(uploadUrl, 'PUT', fileToUpload, getFileType(type, media?.type));

  //   if (!response?.ok) throw new Error('Upload failed');
  // };
  const uploadFile = async (
    uploadUrl: string,
    media: any,
    type: string,
    onProgress?: (percent: number) => void, // ← optional, backward-compatible
  ) => {
    const fileToUpload = type === 'image' ? await fileConverter(media) : media;

    const response = await FetchFileService(
      uploadUrl,
      'PUT',
      fileToUpload,
      getFileType(type, media?.type),
      onProgress, // ← plumb it through
    );

    if (!response?.ok) throw new Error('Upload failed');
  };
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isValidFile = await validateMediaFile(file, 'image');
    if (!isValidFile) {
      setSelectedImage(null);
      dispatch(openModal({ content: 'The uploaded file appears to be corrupted. Please try another file.' }));
      return;
    }
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);

    const timestamp = Date.now();
    const filePath = generateFilePath(file.name, selectedStory?.title, 'image', timestamp);

    dispatch(
      getUploadSignedUrl({ file: filePath, type: getFileType('image', file.type) }, async (res: any) => {
        try {
          await uploadFile(res?.value?.url?.uploadUrl, file, 'image');
          // const fileData = await verifyFile(filePath, user?.token);
          const newData = {
            ...story,
            cover_image: filePath,
            asset_type: file?.type,
          };
          dispatch(updateStory(newData));
        } catch (error) {
          console.error('Error uploading file:', error);
          dispatch(openModal({ content: 'An error occurred during upload.' }));
        } finally {
        }
      }),
    );
  };
  const [hasPopupShown, setHasPopupShown] = useState(localStorage.getItem('popupShown') === 'true');
  const [isPopupOpen, setIsPopupOpen] = useState(openPop);

  useEffect(() => {
    if (!hasPopupShown && openPop) {
      setIsPopupOpen(true);
    }
  }, [openPop, hasPopupShown]);

  const handleClosePop = () => {
    setIsPopupOpen(false);
    setHasPopupShown(true);
    localStorage.setItem('popupShown', 'true');
  };
  const handleOpenColab = () => {
    if (checkPermissions(user?.roles ?? [], 'CLIENT_STORY_UPDATE', story?.id) || user?.id == story?.user_id)
      CloseManageCollaborators();
  };
  const [orientation, setOrientation] = useState<'portrait' | 'landscape' | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const handleImageLoad = () => {
    setProfileLoading(false);
    const img = imageRef.current;
    if (img) {
      const { naturalWidth, naturalHeight } = img;
      if (naturalWidth > naturalHeight) {
        setOrientation('landscape');
      } else {
        setOrientation('portrait');
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        margin: 'auto',
        position: 'relative',
        textAlign: 'center',
        paddingTop: isMobile ? '20px' : '0',
      }}>
      {/* Cover Image (Stretched & Blurred) */}
      {isPopupOpen &&
        (checkPermissions(user?.roles ?? [], 'CLIENT_STORY_CREATE', story?.id) ||
          user?.id === story?.user_id ||
          user?.roles?.some((role: any) => role.story_id === story?.id && role.role.name === 'Story_Admin')) &&
        openPop && <AutoPopup open={openPop} onClose={handleClosePop} />}
      <Box
        px={'1rem'}
        sx={{
          width: '100%',
          height: isMobile ? 'auto' : 468,
          minHeight: isMobile ? '400px' : 468,
          position: 'relative',
          // overflow: 'hidden',
          backgroundColor: 'inherit',
        }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            position: isMobile ? 'relative' : 'absolute',
            top: isMobile ? 0 : 120,
            left: isMobile ? 'auto' : '50%',
            transform: isMobile ? 'none' : 'translateX(-50%)',
            marginBottom: isMobile ? '20px' : '0',
            zIndex: 10,
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          {/* Edit Button (Left) */}
          {!isMobile &&
          !story?.isLocked &&
          (user?.id === story?.user_id ||
            user?.roles?.find((role: any) => role.story_id === story?.id && role.role.name === 'Story_Admin')) ? (
            <MuiIconButton
              icon='/icons/edit-pencil-white'
              label='Edit Memvy'
              altIcon='Memvy'
              iconPosition='left'
              direction='row'
              background='#00000052'
              width={'150.39px'}
              isRounded={false}
              height={43}
              iconHeight={14}
              iconWidth={14}
              sx={{
                borderRadius: '24px',
                '&:hover': {
                  backgroundColor: `${extendedPalette.buttonbackgroundIcon} !important`,
                  borderColor: `${extendedPalette.buttonbackgroundIcon}`,
                },
              }}
              method={EditMemvy}
            />
          ) : (
            <Box></Box>
          )}
          {!story?.isLocked &&
            (user?.id === story?.user_id ||
              user?.roles?.some((role: any) => role.story_id === story?.id && role.role.name === 'Story_Admin')) && (
              <MuiIconButton
                icon={selectedItem?.alticon1}
                label={selectedItem?.alttitle || story?.status}
                iconPosition='left'
                altIcon={selectedItem?.key}
                direction='row'
                background={!isMobile ? '#0000009E' : 'rgba(0, 0, 0, 0.32)'}
                width={'116px'}
                isRounded={false}
                height={43}
                iconHeight={14}
                iconWidth={14}
                endIcon='/icons/down-arrow-light'
                endIconWidth={12}
                endIconHeight={12}
                altEndIcon='down'
                sx={{
                  // top: isMobile ? '-8rem' : '',
                  borderRadius: '24px',
                  gap: '10px',
                  '&:hover': {
                    backgroundColor: `${extendedPalette.buttonbackgroundIcon} !important`,
                    borderColor: `${extendedPalette.buttonbackgroundIcon}`,
                  },
                }}
                method={handleClick}
              />
            )}

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: { width: '200px', background: '#D8E8F6', padding: '8px' },
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            {menuItems.map((item: any, index) => (
              <div key={index}>
                <MenuItem
                  onClick={() => handleSelect(item.title, item.onClick)}
                  className='publish-options'
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { backgroundColor: item.hoverColor },
                  }}>
                  <ListItemText
                    primary={
                      <Typography sx={{ color: item.textColor || 'black', fontWeight: 400 }}>{item.title}</Typography>
                    }
                    secondary={
                      item.secondaryText && (
                        <Typography
                          sx={{
                            color: 'gray',
                            fontSize: '0.85rem',
                          }}>
                          {item.secondaryText}
                        </Typography>
                      )
                    }
                  />
                  <ListItemIcon sx={{ minWidth: '30px', justifyContent: 'flex-end' }}>
                    <Image src={item.icon} alt={item.title} width={20} height={20} />
                  </ListItemIcon>
                </MenuItem>
                {index !== menuItems.length - 1 && <Divider />}
              </div>
            ))}
          </Menu>
        </Box>

        {/* Profile Picture with Square in Middle */}

        <Box
          position='relative'
          top={isMobile ? 0 : 117}
          left={'0'}
          right={'0'}
          display='flex'
          alignContent='center'
          justifyContent='center'
          alignItems='center'>
          <Box
            sx={{
              width: isMobile ? 353 : 391,
              height: 425,
              maxHeight: '425px',
              borderRadius: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 4,
            }}>
            <>
              {profileSrc ? (
                <Box
                  width='100%'
                  sx={{
                    height: 425,
                    maxHeight: '425px',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    position: 'relative',
                  }}>
                  <Image
                    priority
                    fill
                    src={profileSrc}
                    alt=''
                    onLoad={handleImageLoad}
                    ref={imageRef}
                    style={{
                      objectFit: 'cover',
                      opacity: profileLoading ? 0 : 1,
                      maxHeight: '425px',
                    }}
                  />
                </Box>
              ) : (
                <>
                  {selectedStory && (
                    <>
                      {!coverLoading &&
                      (checkPermissions(user?.roles ?? [], 'CLIENT_STORY_CREATE', story?.id) ||
                        user?.id === story?.user_id ||
                        user?.roles?.some(
                          (role: any) => role.story_id === story?.id && role.role.name === 'Story_Admin',
                        )) ? (
                        <Box
                          sx={{
                            backgroundImage: selectedImage
                              ? `url(${selectedImage})`
                              : selectedStory.icon
                              ? `url(${selectedStory.icon})`
                              : 'none',
                            backgroundPosition: selectedStory.position || 'center',
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: 'rgba(254, 254, 254, 0.2)',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '10px',
                            border: !selectedImage ? '2px dashed rgba(217, 217, 217, 1)' : '',
                            cursor: 'pointer',
                            flexDirection: 'column',
                            gap: 1,
                            position: 'relative',
                          }}
                          onClick={() => document.getElementById('fileInput')?.click()}>
                          <input
                            type='file'
                            id='fileInput'
                            accept='image/*'
                            style={{ display: 'none' }}
                            onChange={handleImageUpload}
                          />

                          {!selectedImage && (
                            <>
                              {/* SVG Icon */}
                              <Box
                                component='img'
                                src='/icons/upload-white.svg'
                                alt='Upload'
                                sx={{ width: 32, height: 32, opacity: 0.8 }}
                              />

                              {/* Upload Text */}
                              <Typography sx={{ color: 'white', fontSize: '16px' }}>Upload a Cover Photo</Typography>
                            </>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <Image src={selectedStory?.icon} alt={selectedStory?.label} width={350} height={234} />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </>

            {!story?.isLocked &&
            (user?.id === story?.user_id ||
              user?.roles?.find((role: any) => role.story_id === story?.id && role.role.name === 'Story_Admin')) &&
            isMobile ? (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  right: 20,
                  zIndex: 5,
                }}>
                <MuiIconButton
                  icon='/icons/edit-pencil-white'
                  label='Edit'
                  altIcon='Memvy'
                  iconPosition='left'
                  direction='row'
                  background='rgba(0, 0, 0, 0.32)'
                  width={'91.39px'}
                  isRounded={false}
                  height={43}
                  iconHeight={18}
                  iconWidth={18}
                  sx={{
                    borderRadius: '24px',
                    padding: '0',
                    minWidth: '32px',
                    height: '32px',
                    backgroundColor: 'rgba(0, 0, 0, 0.32)',
                    '&:hover': {
                      backgroundColor: `${extendedPalette.buttonbackgroundIcon} !important`,
                      borderColor: `${extendedPalette.buttonbackgroundIcon}`,
                    },
                  }}
                  method={EditMemvy}
                />
              </Box>
            ) : (
              <Box></Box>
            )}
          </Box>
        </Box>

        {/* cover */}
        {!isMobile && coverSrc && (
          <>
            {coverLoading && (
              <Box width={'100%'} height={'343px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <CircularProgress sx={{ color: '#fff' }} />
              </Box>
            )}

            <Image
              priority
              src={coverSrc}
              fill={true}
              alt=''
              onLoad={() => setCoverLoading(false)}
              style={{
                width: '100%',
                height: '100%',
                maxHeight: '343px',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: coverLoading ? 0 : 0.9,
                transition: 'opacity 0.6s ease-in',
              }}
            />

            {/* Blur Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '343px',
                backdropFilter: coverLoading ? '' : 'blur(10px)',

                zIndex: 3,
              }}
            />
          </>
        )}
      </Box>

      {/* Inform ation Section */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '640px',
          mx: 'auto',
          padding: '10px',
          marginTop: isMobile ? 0 : '80px',
        }}>
        <Typography
          // mt={!isMobile ? '52px' : 6}
          lineHeight={'1.25'}
          fontSize={!isMobile ? '3.75rem' : '2rem'}
          fontWeight={400}
          color='#ffffff'>
          {title}
        </Typography>
        <Typography
          fontSize={'1rem'}
          fontWeight={400}
          sx={{ color: '#F5F5F5', marginTop: '0.8rem', padding: '0 10px' }}>
          {description}
        </Typography>
        {createdDate && (
          <Typography
            fontSize={!isMobile ? '1rem' : '14px'}
            sx={{ color: 'rgba(241, 247, 250, 1)', marginTop: '0.8rem', mb: '1rem', opacity: '60%' }}>
            Created {formatDate(createdDate)}
          </Typography>
        )}
        {story?.memoriesCount !== undefined && story?.memoriesCount !== null && (
          // <></>
          <Box display='flex' gap='20px' alignItems='center' justifyContent='center' width='100%'>
            {/* Eye Icon + Text */}
            <Box display='flex' alignItems='center'>
              <Box component='img' src='/icons/eye.svg' alt='Upload' sx={{ width: 25, height: 25, opacity: 0.8 }} />
              <Typography sx={{ color: 'white', fontWeight: '400', fontSize: '16px', ml: '6px' }}>
                {story?.memoriesCount} Memories
              </Typography>
            </Box>

            {/* Hourglass Icon + Text */}
            <Box display='flex' alignItems='center'>
              <Box component='img' src='/icons/Hourglass.svg' alt='wait' sx={{ width: 20, height: 18, opacity: 0.8 }} />
              <Typography sx={{ color: 'white', fontWeight: '400', fontSize: '16px', ml: '6px' }}>
                {story?.unapprovedMemoryCount} Pending approval
              </Typography>
            </Box>
          </Box>
        )}
        {!isMobile ? (
          <>
            {images && (
              <Box
                sx={{
                  marginTop: '0.8rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: images.length > 0 ? '4rem' : '0.4rem',
                  gap: '6.5px',
                }}>
                <AvatarGroup onClick={handleOpenColab} sx={{ cursor: 'pointer' }} max={6} spacing={1}>
                  {images.map((collab: any, index: any) => (
                    <Box key={index} sx={{ position: 'relative', display: 'inline-block', mr: 1 }}>
                      <Tooltip title={collab?.title} arrow enterDelay={100} leaveDelay={0}>
                        <Avatar
                          alt={collab?.alt}
                          src={collab?.src}
                          sx={{
                            fontSize: '1rem',
                            width: { xs: 36, sm: 32 },
                            height: { xs: 36, sm: 32 },
                          }}>
                          {collab?.alt}
                        </Avatar>
                      </Tooltip>
                      {story?.user_id === collab?.id && (
                        <Box
                          component='img'
                          src='/icons/Star.svg'
                          alt='Star'
                          sx={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            width: 16,
                            height: 16,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </AvatarGroup>

                {!story?.isLocked &&
                  (user?.id === story?.user_id ||
                    checkPermissions(user?.roles ?? [], 'CLIENT_STORY_UPDATE', story?.id)) && (
                    <MuiIconButton
                      icon='/icons/collab'
                      label='Invite'
                      altIcon='Invite'
                      iconPosition='left'
                      direction='row'
                      background='#00000052'
                      width={'102px'}
                      isRounded={false}
                      height={43}
                      iconHeight={15}
                      iconWidth={15}
                      sx={{
                        borderRadius: '24px',
                        '&:hover': {
                          backgroundColor: `${extendedPalette.buttonbackgroundIcon} !important`,
                          borderColor: `${extendedPalette.buttonbackgroundIcon}`,
                        },
                      }}
                      method={() => dispatch(openPublishModal())}
                    />
                  )}
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '115px',
              width: '100vw',
              position: 'fixed',
              bottom: 0,
              zIndex: 1000,
              left: 0,
              padding: '10px 16px',
              overflow: 'hidden',
            }}>
            {/* Background Container (with Blur Effect) */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255, 255, 255, 0.2)',
                filter: 'blur(1px)',
                backdropFilter: 'blur(40px)',
                zIndex: -1,
              }}
            />

            {/* Left Section - Collaborators Heading & Avatars */}
            <Box
              onClick={handleOpenColab}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                paddingLeft: 0,
                cursor: 'pointer',
              }}>
              {/* Heading */}
              <Typography
                variant='body2'
                sx={{
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: '#ffffff',
                  marginBottom: '4px',
                }}>
                Collaborators
              </Typography>

              {/* Avatar Group */}
              {images && (
                <AvatarGroup max={6} spacing={1} sx={{ marginLeft: 0 }}>
                  {images.map((collab: any, index: any) => (
                    <Box key={index} sx={{ position: 'relative', display: 'inline-block', marginRight: '0.5rem' }}>
                      <Tooltip title={collab?.alt} arrow enterDelay={100} leaveDelay={0}>
                        <Avatar
                          alt={collab?.alt}
                          src={collab?.src}
                          sx={{
                            width: { xs: 36, sm: 32 },
                            height: { xs: 36, sm: 32 },
                          }}>
                          {collab?.alt}
                        </Avatar>
                      </Tooltip>
                      {story?.user_id === collab?.id && (
                        <Box
                          component='img'
                          src='/icons/Star.svg'
                          alt='Star'
                          sx={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            width: 16,
                            height: 16,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </AvatarGroup>
              )}
            </Box>

            {/* Invite Button - Aligned to Right */}
            {!story?.isLocked &&
              (user?.id === story?.user_id ||
                user?.roles?.find((role: any) => role.story_id === story?.id && role.role.name === 'Story_Admin')) && (
                <MuiIconButton
                  icon='/icons/collab'
                  label='Invite'
                  altIcon='Invite'
                  iconPosition='left'
                  direction='row'
                  background='#00000052'
                  width={'102px'}
                  isRounded={false}
                  height={43}
                  iconHeight={15}
                  iconWidth={15}
                  sx={{
                    borderRadius: '24px',
                    alignSelf: 'center',
                    marginTop: '15px',
                    '&:hover': {
                      backgroundColor: `${extendedPalette.buttonbackgroundIcon} !important`,
                      borderColor: `${extendedPalette.buttonbackgroundIcon}`,
                    },
                  }}
                  method={() => dispatch(openPublishModal())}
                />
              )}
          </Box>
        )}
      </Box>
      <DeleteStoryModal
        open={openDeleteModal}
        onClose={() => {
          SetOpenDeleteModal(false);
        }}
        confirmMethod={deleteMemvy}
      />
    </Box>
  );
};

export default StoryHeader;
